// Live progress for v2panda-promoter publish/rollback/copy tasks.
// The worker streams NDJSON when asked with `accept: application/x-ndjson`:
// one {type:'progress'} line per step and a final {type:'result'} line.
// Older workers reply with plain JSON; both shapes end in the same result.

export type PromoterPhase =
  | 'load-build' | 'load-archive'
  | 'check-r2' | 'check-oss' | 'upload-oss'
  | 'archive' | 'write-r2' | 'write-oss'
  | 'verify'

export type PromoterProgressEvent = {
  type: 'progress'
  phase: PromoterPhase
  key?: string
  at?: number
}

export type PromoterResult = { status: number, body: any }

export type ProgressStepState = 'running' | 'done' | 'failed'

export type ProgressStep = {
  id: string
  label: string
  state: ProgressStepState
  startedAt: number
  endedAt?: number
}

export type ProgressState = {
  title: string
  status: 'running' | 'done' | 'failed'
  startedAt: number
  endedAt?: number
  steps: ProgressStep[]
  summary: string
}

const PHASE_LABELS: Record<PromoterPhase, string> = {
  'load-build': '读取构建',
  'load-archive': '读取归档',
  'check-r2': '检查 R2',
  'check-oss': '检查 OSS',
  'upload-oss': '上传到 OSS',
  'archive': '归档线上 yml',
  'write-r2': '写入 R2',
  'write-oss': '写入 OSS',
  'verify': '核对 SHA-256',
}

// Worker-streamed steps; client-side steps (uploads) use their own ids.
const STREAM_STEP_PREFIX = 'stream:'

const baseName = (key: string) => key.split('/').filter(Boolean).pop() || key

export function progressLabel(event: Pick<PromoterProgressEvent, 'phase' | 'key'>): string {
  const label = PHASE_LABELS[event.phase] || event.phase
  return event.key ? `${label} · ${baseName(event.key)}` : label
}

export function createProgressState(title: string, now = Date.now()): ProgressState {
  return { title, status: 'running', startedAt: now, steps: [], summary: '' }
}

export function startStep(state: ProgressState, id: string, label: string, now = Date.now()) {
  state.steps.push({ id, label, state: 'running', startedAt: now })
}

export function endStep(state: ProgressState, id: string, ok: boolean, now = Date.now(), label?: string) {
  const step = state.steps.find((entry) => entry.id === id && entry.state === 'running')
  if (!step) return
  step.state = ok ? 'done' : 'failed'
  step.endedAt = now
  if (label) step.label = label
}

// A worker step lasts until the next one starts. A repeat of the same phase
// and key updates the running step in place.
export function applyProgressEvent(state: ProgressState, event: PromoterProgressEvent, now = Date.now()) {
  const id = `${STREAM_STEP_PREFIX}${event.phase}:${event.key || ''}`
  const label = progressLabel(event)
  const running = [...state.steps].reverse().find((step) => step.id.startsWith(STREAM_STEP_PREFIX) && step.state === 'running')
  if (running?.id === id) {
    running.label = label
    return
  }
  if (running) endStep(state, running.id, true, now)
  startStep(state, id, label, now)
}

export function finishProgress(state: ProgressState, ok: boolean, summary: string, now = Date.now()) {
  for (const step of state.steps) {
    if (step.state !== 'running') continue
    step.state = ok ? 'done' : 'failed'
    step.endedAt = now
  }
  state.status = ok ? 'done' : 'failed'
  state.summary = summary
  state.endedAt = now
}

export async function readPromoterResponse(
  response: Response,
  onEvent: (event: PromoterProgressEvent) => void,
): Promise<PromoterResult> {
  if (!(response.headers.get('content-type') || '').includes('application/x-ndjson')) {
    const text = await response.text()
    let body: any = text
    try {
      body = text ? JSON.parse(text) : {}
    } catch {
      body = { error: text || `HTTP ${response.status}` }
    }
    return { status: response.status, body }
  }
  if (!response.body) throw new Error('promoter 返回了空的进度流')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: PromoterResult | null = null
  const handleLine = (line: string) => {
    if (!line.trim()) return
    const event = JSON.parse(line)
    if (event.type === 'result') result = { status: event.status, body: event.body }
    else if (event.type === 'progress') onEvent(event)
  }
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let newline = buffer.indexOf('\n')
    while (newline >= 0) {
      handleLine(buffer.slice(0, newline))
      buffer = buffer.slice(newline + 1)
      newline = buffer.indexOf('\n')
    }
  }
  handleLine(buffer + decoder.decode())
  if (!result) throw new Error('promoter 进度流中断，没有返回结果；请刷新后确认线上状态')
  return result
}
